import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import DownloadIcon from "@mui/icons-material/Download";
import { Box, Button, CircularProgress, Stack, Typography } from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { generatePath, useNavigate, useParams, useSearchParams } from "react-router";
import AppLayout from "@/components/AppLayout";
import ErrorPage from "@/components/ErrorPage";
import { useCampground } from "@/contexts/AppConfigContext";
import { useBookingContext } from "@/contexts/BookingContext";
import { useFeatureFlags } from "@/contexts/FeaturesContext";
import { useBookingDetails } from "@/hooks/useBookingDetails";
import { useStepNavigation } from "@/hooks/useStepNavigation";
import { notifyAdmin } from "@/utils/adminWidget";
import { analytics } from "@/utils/analytics";
import { buildDemoBookingDetails } from "@/utils/booking";
import { logError } from "@/utils/logError";
import BookingDetails from "./components/BookingDetails";
import ConfirmationSkeleton from "./components/ConfirmationSkeleton";

export default function Confirmation() {
  const params = useParams<{
    bookingId: string;
    language: string;
    campground: string;
  }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isAdmin = searchParams.get("mcadmin") === "1" && window.self !== window.top;
  const { state, actions } = useBookingContext();
  const { data: campgroundData } = useCampground();
  const { goToStep } = useStepNavigation();
  const { data: bookingDetails, loading, fetchBookingDetails } = useBookingDetails();
  const { flags } = useFeatureFlags();
  const [fetchError, setFetchError] = useState(false);

  useEffect(() => {
    if (!params.bookingId && state.bookingId) {
      if (flags.demoMode) {
        const details = buildDemoBookingDetails(state);
        sessionStorage.setItem("campnerd_demo_booking_details", JSON.stringify(details));
      }
      const path = generatePath("/:language/:campground/booking/confirmation/:bookingId", {
        language: params.language!,
        campground: params.campground!,
        bookingId: state.bookingId,
      });
      actions.resetBooking();
      const search = searchParams.toString();
      navigate(search ? `${path}?${search}` : path, { replace: true });
    }
  }, [
    actions.resetBooking,
    flags.demoMode,
    navigate,
    params.bookingId,
    params.campground,
    params.language,
    searchParams,
    state,
  ]);

  useEffect(() => {
    if (params.bookingId) {
      analytics.capture("booking_completed", {
        booking_id: params.bookingId,
      });
      fetchBookingDetails(params.bookingId).catch(() => setFetchError(true));
    }
  }, [params.bookingId, fetchBookingDetails]);

  useEffect(() => {
    if (isAdmin && bookingDetails?.booking_status === "confirmed" && params.bookingId) {
      notifyAdmin("campnerd.booking-created", params.bookingId);
    }
  }, [isAdmin, bookingDetails?.booking_status, params.bookingId]);

  const captureRef = useRef<HTMLDivElement>(null);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (!generating || !captureRef.current || !bookingDetails) return;

    const runCapture = async () => {
      try {
        const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
          import("html2canvas"),
          import("jspdf"),
        ]);
        const canvas = await html2canvas(captureRef.current!, {
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: "#ffffff",
        });
        const imgData = canvas.toDataURL("image/jpeg", 0.92);
        const pdf = new jsPDF({
          orientation: "portrait",
          unit: "px",
          format: [canvas.width / 2, canvas.height / 2],
        });
        pdf.addImage(imgData, "JPEG", 0, 0, canvas.width / 2, canvas.height / 2);
        pdf.save(`booking-${bookingDetails.booking_number}.pdf`);
      } catch (err) {
        logError(err);
        enqueueSnackbar(t`Could not generate the PDF. Please try again.`, { variant: "error" });
      } finally {
        setGenerating(false);
      }
    };

    runCapture();
  }, [generating, bookingDetails]);

  const handleDownload = () => setGenerating(true);

  if (loading) {
    return (
      <AppLayout showAppBar={false} centerContent>
        <ConfirmationSkeleton />
      </AppLayout>
    );
  }

  if (fetchError || !bookingDetails) {
    return <ErrorPage message={t`Could not load booking details`} />;
  }

  if (bookingDetails.booking_status !== "confirmed") {
    return (
      <AppLayout showAppBar={false} centerContent>
        <Stack spacing={3} alignItems="center" textAlign="center" p={3}>
          <Box>
            {campgroundData.urls?.image && (
              <img src={campgroundData.urls.image} alt={t`CampNerd`} style={{ height: 100 }} />
            )}
          </Box>
          <Typography variant="h3" sx={{ fontWeight: 600 }}>
            <Trans>Your booking could not be confirmed</Trans>
          </Typography>
          <Typography variant="body1" color="text.secondary">
            <Trans>
              Your reservation time expired before the payment was completed, so the booking was not
              confirmed.
              <br />
              Please start a new booking to try again.
            </Trans>
          </Typography>
          <Button variant="contained" size="large" onClick={() => goToStep("dates")}>
            <Trans>Start a new booking</Trans>
          </Button>
        </Stack>
      </AppLayout>
    );
  }

  return (
    <AppLayout showAppBar={false} contentMaxWidth="md" centerContent>
      <Stack
        alignItems="center"
        sx={{ "@media print": { flexDirection: "row", alignItems: "flex-start", gap: 4 } }}
      >
        <Box>
          {campgroundData.urls?.image && (
            <img src={campgroundData.urls.image} alt={t`CampNerd`} style={{ height: 100 }} />
          )}
        </Box>
        <Stack spacing={2} sx={{ "@media print": { flex: 1 } }}>
          <Stack
            alignItems="center"
            spacing={1}
            sx={{ "@media print": { alignItems: "flex-end" } }}
          >
            <Typography variant="h3" className="no-print" sx={{ fontWeight: 600 }}>
              <Trans>Thank you for your booking!</Trans>
            </Typography>
            <Typography
              variant="h3"
              className="print-only"
              sx={{ fontWeight: 600, color: "text.accent" }}
            >
              <Trans>Your booking at a glance</Trans>
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ mt: 1, textAlign: "center", "@media print": { textAlign: "left" } }}
            >
              <Trans>You will receive a booking confirmation via email shortly.</Trans>
            </Typography>
            <Typography variant="body1" color="text.secondary" gutterBottom sx={{ mt: 3 }}>
              <Trans>
                Your booking number is: <strong>{bookingDetails.booking_number}</strong>
              </Trans>
            </Typography>
          </Stack>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            className="no-print"
            sx={{ width: "100%" }}
            spacing={2}
          >
            {isAdmin && (
              <Button
                variant="contained"
                size="large"
                onClick={() => notifyAdmin("campnerd.back-to-bookings")}
              >
                <Trans>Back to bookings</Trans>
              </Button>
            )}
            <Button
              variant="contained"
              size="large"
              color="primary"
              onClick={() => goToStep("dates")}
              sx={{ px: { xs: 0, sm: 5 } }}
            >
              <Trans>Start a new booking</Trans>
            </Button>
            <Button
              variant="contained"
              size="large"
              startIcon={
                generating ? <CircularProgress size={18} color="inherit" /> : <DownloadIcon />
              }
              onClick={handleDownload}
              disabled={generating}
              sx={{ px: { xs: 0, sm: 5 } }}
            >
              {generating ? <Trans>Generating…</Trans> : <Trans>Download confirmation</Trans>}
            </Button>
          </Stack>
        </Stack>
      </Stack>

      {generating &&
        createPortal(
          <div
            ref={captureRef}
            style={{
              position: "fixed",
              left: "-9999px",
              top: 0,
              width: "780px",
              background: "#fff",
              padding: "32px",
            }}
          >
            <BookingDetails bookingDetails={bookingDetails!} campgroundData={campgroundData} />
          </div>,
          document.body,
        )}
    </AppLayout>
  );
}
